/**
 * Service for managing Qualifier API operations
 * Implements all endpoints from the Qualifier API Frontend Integration Guide
 */

import { makeAuthenticatedRequest } from './backendApiService';
import { convertUTCDatesToLocal } from '../utils/dateUtils';

const QUALIFIER_BASE_URL = '/api/qualifiers';

/**
 * Helper to convert dates in qualifier objects from UTC to local
 * @param {Object|Array} data - Qualifier or array of qualifiers
 * @returns {Object|Array} Data with converted dates
 */
const convertQualifierDates = (data) => {
    return convertUTCDatesToLocal(data, ['createdAt', 'lastMod']);
};

/**
 * Qualifier Service Class
 */
class QualifierService {
    /**
     * Get all qualifiers (active and inactive)
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of qualifier objects
     */
    async getAll(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, QUALIFIER_BASE_URL);
            return convertQualifierDates(response);
        } catch (error) {
            console.error('Failed to fetch all qualifiers:', error);
            throw error;
        }
    }

    /**
     * Get only active qualifiers
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of active qualifier objects
     */
    async getActive(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${QUALIFIER_BASE_URL}/active`
            );
            return convertQualifierDates(response);
        } catch (error) {
            console.error('Failed to fetch active qualifiers:', error);
            throw error;
        }
    }

    /**
     * Get only inactive qualifiers
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of inactive qualifier objects
     */
    async getInactive(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${QUALIFIER_BASE_URL}/inactive`
            );
            return convertQualifierDates(response);
        } catch (error) {
            console.error('Failed to fetch inactive qualifiers:', error);
            throw error;
        }
    }

    /**
     * Get qualifier by ID
     * @param {string} accessToken - JWT access token
     * @param {number} id - Qualifier ID
     * @returns {Promise<Object>} Qualifier object
     */
    async getById(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${QUALIFIER_BASE_URL}/${id}`
            );
            return convertQualifierDates(response);
        } catch (error) {
            console.error(`Failed to fetch qualifier with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Get qualifier by code
     * @param {string} accessToken - JWT access token
     * @param {string} code - Qualifier code
     * @returns {Promise<Object>} Qualifier object
     */
    async getByCode(accessToken, code) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${QUALIFIER_BASE_URL}/code/${encodeURIComponent(code)}`
            );
            return convertQualifierDates(response);
        } catch (error) {
            console.error(`Failed to fetch qualifier with code ${code}:`, error);
            throw error;
        }
    }

    /**
     * Create a new qualifier
     * @param {string} accessToken - JWT access token
     * @param {Object} qualifierData - Qualifier data
     * @param {string} qualifierData.qualifierCode - Qualifier code (required, max 50 chars)
     * @param {string} qualifierData.description - Description (optional, max 254 chars)
     * @param {boolean} qualifierData.active - Active status (optional, defaults to true)
     * @returns {Promise<Object>} Created qualifier object
     */
    async create(accessToken, qualifierData) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                QUALIFIER_BASE_URL,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(qualifierData)
                }
            );
            return convertQualifierDates(response);
        } catch (error) {
            console.error('Failed to create qualifier:', error);
            throw error;
        }
    }

    /**
     * Update an existing qualifier
     * @param {string} accessToken - JWT access token
     * @param {number} id - Qualifier ID
     * @param {Object} qualifierData - Updated qualifier data
     * @param {string} qualifierData.qualifierCode - Qualifier code (required, max 50 chars)
     * @param {string} qualifierData.description - Description (optional, max 254 chars)
     * @param {boolean} qualifierData.active - Active status
     * @returns {Promise<Object>} Updated qualifier object
     */
    async update(accessToken, id, qualifierData) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${QUALIFIER_BASE_URL}/${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(qualifierData)
                }
            );
            return convertQualifierDates(response);
        } catch (error) {
            console.error(`Failed to update qualifier with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Delete a qualifier
     * @param {string} accessToken - JWT access token
     * @param {number} id - Qualifier ID
     * @returns {Promise<Object>} Delete response
     */
    async delete(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${QUALIFIER_BASE_URL}/${id}`,
                {
                    method: 'DELETE'
                }
            );
            return response;
        } catch (error) {
            console.error(`Failed to delete qualifier with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Activate a qualifier
     * @param {string} accessToken - JWT access token
     * @param {number} id - Qualifier ID
     * @returns {Promise<Object>} Activated qualifier object
     */
    async activate(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${QUALIFIER_BASE_URL}/${id}/activate`,
                {
                    method: 'PATCH'
                }
            );
            return convertQualifierDates(response);
        } catch (error) {
            console.error(`Failed to activate qualifier with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Deactivate a qualifier
     * @param {string} accessToken - JWT access token
     * @param {number} id - Qualifier ID
     * @returns {Promise<Object>} Deactivated qualifier object
     */
    async deactivate(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${QUALIFIER_BASE_URL}/${id}/deactivate`,
                {
                    method: 'PATCH'
                }
            );
            return convertQualifierDates(response);
        } catch (error) {
            console.error(`Failed to deactivate qualifier with ID ${id}:`, error);
            throw error;
        }
    }
}

// Export singleton instance
const qualifierService = new QualifierService();
export default qualifierService;
